import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import './NotificationCenter.css';

type Notification = {
  id: string;
  title: string;
  message: string;
  link?: string | null;
  readAt?: string | null;
  createdAt: string;
};

export default function NotificationCenter(): React.ReactElement | null {
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);

  const refreshCount = () => {
    if (!authenticated) return;
    apiClient
      .get('/notifications/count')
      .then((r) => setUnread(r.data.unread))
      .catch(() => undefined);
  };
  const load = () => {
    apiClient
      .get('/notifications')
      .then((r) => setItems(r.data))
      .catch(() => undefined);
  };
  useEffect(() => {
    refreshCount();
    const timer = window.setInterval(refreshCount, 45000);
    return () => window.clearInterval(timer);
  }, [authenticated, location.pathname]);
  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!authenticated || location.pathname === '/login') return null;
  const readOne = async (item: Notification) => {
    if (!item.readAt) {
      await apiClient.patch(`/notifications/${item.id}/read`);
      setUnread((value) => Math.max(0, value - 1));
    }
    setOpen(false);
    if (item.link?.startsWith('/')) navigate(item.link);
  };
  const readAll = async () => {
    await apiClient.patch('/notifications/read-all');
    setUnread(0);
    setItems((current) => current.map((item) => ({ ...item, readAt: new Date().toISOString() })));
  };

  return (
    <div className="notification-center">
      <button
        className="notification-bell"
        onClick={() => setOpen(!open)}
        aria-label="Notificaciones"
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 && <b>{unread > 99 ? '99+' : unread}</b>}
      </button>
      {open && (
        <div className="notification-panel">
          <div className="notification-title">
            <strong>Notificaciones</strong>
            <button onClick={readAll} disabled={!unread}>
              Marcar todas como leídas
            </button>
          </div>
          <div className="notification-list">
            {!items.length && <p className="notification-empty">No tenés notificaciones.</p>}
            {items.map((item) => (
              <button
                key={item.id}
                className={`notification-item ${item.readAt ? '' : 'is-unread'}`}
                onClick={() => readOne(item)}
              >
                <span>{item.title}</span>
                <small>{item.message}</small>
                <time>{new Date(item.createdAt).toLocaleString('es-AR')}</time>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
